import type { Scene } from '../render/types';


let sent = false;
let lastCheckMs = 0;
const CHECK_INTERVAL_MS = 1000;
const FIRST_CHECK_DELAY_MS = 800;

let firstFrameMs = 0;

const mapOf = (src: string): string => (src.split('?')[0].match(/bg_([a-z0-9_]+)\.webp$/i)?.[1] ?? src);

const deviceMemoryOf = (nav: Navigator | undefined): number => {
  if (!nav) return 0;
  const raw: unknown = Reflect.get(nav, 'deviceMemory');
  return typeof raw === 'number' ? raw : 0;
};

function maxBackdropAlpha(cv: HTMLCanvasElement): number {
  try {
    const cx = cv.getContext('2d');
    if (!cx) return 0;
    let max = 0;
    for (let gy = 1; gy <= 3; gy++) {
      for (let gx = 1; gx <= 3; gx++) {
        const x = Math.floor((cv.width * gx) / 4);
        const y = Math.floor((cv.height * gy) / 4);
        const a = cx.getImageData(x, y, 1, 1).data[3];
        if (a > max) max = a;
      }
    }
    return max;
  } catch {
    return -1;
  }
}

export function checkBlackBackdrop(sc: Scene, cw: number, ch: number, dpr: number): void {
  if (sent || typeof performance === 'undefined') return;
  const t = performance.now();
  if (firstFrameMs === 0) firstFrameMs = t;
  if (t - firstFrameMs < FIRST_CHECK_DELAY_MS) return;
  if (t - lastCheckMs < CHECK_INTERVAL_MS) return;
  lastCheckMs = t;

  const bg = sc.bg;
  const cv = sc.backdrop?.canvas ?? null;
  const bgW = bg.naturalWidth;
  const alpha = cv ? maxBackdropAlpha(cv) : 0;

  let verdict: string;
  if (bgW === 0) verdict = 'bg_decode_fail';
  else if (!cv) verdict = 'no_backdrop_canvas';
  else if (alpha === -1) verdict = 'getimagedata_throw';
  else if (alpha === 0) verdict = 'backdrop_blank';
  else return;

  sent = true;
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const payload = {
    verdict,
    map: mapOf(bg.src),
    bgNaturalWidth: bgW,
    bgComplete: bg.complete,
    backdropAlpha: alpha,
    hasBackdropCanvas: !!cv,
    backdropW: cv?.width ?? 0,
    backdropH: cv?.height ?? 0,
    cw: Math.round(cw),
    ch: Math.round(ch),
    dpr,
    deviceMemory: deviceMemoryOf(nav),
    hardwareConcurrency: nav?.hardwareConcurrency ?? 0,
    userAgent: nav?.userAgent ?? '',
  };

  void import('@datadog/browser-rum')
    .then(({ datadogRum }) => datadogRum.addAction('rushmid_black_backdrop', payload))
    .catch(() => { });
}
